import { runRenameChatWorkflow } from "@/action-dialog";
import { restoreErrorMessage, useRestoreFlowSession } from "@/api/archived";
import {
  useCreateFlowSession,
  useDeleteFlowSession,
  useUpdateWorkspaceInboxItem,
} from "@/api/flow_sessions";
import { useSetFlowSessionFolder } from "@/api/folders";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { ROUTES } from "@/constants/routes";
import { useOpenChat } from "@/hooks/useOpenChat";
import { route } from "@/utils/route";
import { FLOW_SESSION_MODES, InboxItem } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export function useChatCardActions({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const { openChat } = useOpenChat();
  const updateInboxItem = useUpdateWorkspaceInboxItem();

  const createFlowSession = useCreateFlowSession({
    onError: () => toast.error("Failed to create chat"),
  });
  const setFlowSessionFolder = useSetFlowSessionFolder();

  const deleteFlowSession = useDeleteFlowSession({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["workspace-flow-sessions", workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-flow-sessions", "recent"],
      });
    },
    onError: () => toast.error("Failed to delete chat"),
  });
  const restoreFlowSession = useRestoreFlowSession({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });

  // Creating from inside a folder view files the new chat there. The chat
  // exists either way, so a filing failure only means it landed at the
  // workspace root — say so instead of failing silently (mirrors the rail's
  // handleNewChat).
  const handleCreate = useCallback(
    (folderId?: string) => {
      if (!workspaceId) return;
      createFlowSession.mutate(
        {
          mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
          class: "workspace",
          workspace_id: workspaceId,
        },
        {
          onSuccess: (session) => {
            if (folderId) {
              setFlowSessionFolder.mutate(
                { flowSessionId: session.id, folderId, workspaceId },
                {
                  onError: () =>
                    toast.error(
                      "Chat created at the workspace root — couldn't file it into the folder.",
                    ),
                },
              );
            }
            openChat(session.id, { mode: "replace", source: "new-menu" });
          },
        },
      );
    },
    [createFlowSession, workspaceId, setFlowSessionFolder, openChat],
  );

  const handleOpen = useCallback(
    (item: InboxItem) => {
      if (!item.is_read) {
        updateInboxItem.mutate({ id: item.id, is_read: true });
      }
      openChat(item.flow_session_id, { source: "browser-row" });
    },
    [openChat, updateInboxItem],
  );

  // One-click move-to-trash with Undo in the toast — no confirmation dialog,
  // since trashed chats stay restorable.
  const handleDelete = useCallback(
    (item: InboxItem) => {
      const flowSessionId = item.flow_session_id;
      deleteFlowSession.mutate(flowSessionId, {
        onSuccess: () =>
          showMovedToTrashToast(() => restoreFlowSession.mutate(flowSessionId)),
      });
    },
    [deleteFlowSession, restoreFlowSession],
  );

  const handleRename = useCallback((item: InboxItem) => {
    window.setTimeout(() => void runRenameChatWorkflow({ item }), 0);
  }, []);

  return {
    isCreating: createFlowSession.isPending,
    isDeleting: deleteFlowSession.isPending,
    canCreate: !!workspaceId,
    onCreate: handleCreate,
    onOpen: handleOpen,
    onDelete: handleDelete,
    onRename: handleRename,
    dialogs: null,
  };
}

/**
 * Create a chat in a given workspace (defaults to the private workspace at the
 * call site). `openAs` controls where it lands: `"auto"` (default) routes
 * through the floating-vs-tab policy, while `"tab"` always navigates — used by
 * the tab strip's "New tab → New Chat", whose whole gesture is opening a tab.
 */
export function useCreatePrivateWorkspaceChat() {
  const navigate = useNavigate();
  const { openChat } = useOpenChat();

  const createFlowSession = useCreateFlowSession({
    onError: () => toast.error("Failed to create chat"),
  });

  return {
    create: (workspaceId: string, opts?: { openAs?: "auto" | "tab" }) => {
      if (!workspaceId) return;
      createFlowSession.mutate(
        {
          mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
          class: "workspace",
          workspace_id: workspaceId,
        },
        {
          onSuccess: (session) => {
            if (opts?.openAs === "tab") {
              navigate(
                route(ROUTES.CHAT.DETAIL, { flowSessionId: session.id }),
              );
            } else {
              openChat(session.id, { mode: "replace", source: "new-menu" });
            }
          },
        },
      );
    },
    isPending: createFlowSession.isPending,
  };
}
